import Vue from "vue";
import Router from "vue-router";
import base from "@/components/base";
import { store } from "@/vuex/store.js";
import { closeInfoSidebar, clearGeneralFilter } from "@/utils/router_middleware";
const Layout = () => import("@/components/layout");

import RawBunchDetail from "@/components/Palm/PalmRawDetail/RawBunchDetail.vue";
import RawCaroteneDetail from "@/components/Palm/PalmRawDetail/RawCaroteneDetail.vue";
import RawFattyAcidDetail from "@/components/Palm/PalmRawDetail/RawFattyAcidDetail.vue";
import RawVegetativeDetail from "@/components/Palm/PalmRawDetail/RawVegetativeDetail.vue";
import RawVitaminDetail from "@/components/Palm/PalmRawDetail/RawVitaminDetail.vue";
import RawYieldDetail from "@/components/Palm/PalmRawDetail/RawYieldDetail.vue";

Vue.use(Router);

const router = new Router({
    mode: "history",
    routes: [
        {
            path: "/",
            redirect: "/dashboard"
        },
        {
            path: "/login",
            name: "Login",
            component: () => import("@/components/login"),
        },
        {
            path: "/user-activation",
            name: "UserActivation",
            component: () => import("@/components/Public/UserActivation.vue"),
            meta: { 
                requireAuth: false,
            }
        },
        {
            path: "/user-reset-password",
            name: "UserResetPassword",
            component: () => import("@/components/Public/UserResetPassword.vue"),
            meta: { 
                requireAuth: false,
            }
        },
        {
            path: "/error",
            name: "Error404",
            component: () => import("@/components/Error/ErrorPage.vue"),
            meta: { 
                requireAuth: false,
            }
        },
        {
            path: "/protected-base",
            name: "ProtectedBase",
            component: Layout,
            children: [
                {
                    path: "/dashboard",
                    name: "Dashboard",
                    component: () => import("@/components/Dashboard/DashboardV2.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/sp-dashboard",
                    name: "Seed Dashboard",
                    component: () => import("@/components/SeedDashboard/Dashboard.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/allStationChartsSummary",
                    name: "AllStationCharts",
                    component: () => import("@/components/Dashboard/AllStationCharts.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/trial/trial-list",
                    name: "TrialList",
                    component: () => import("@/components/Trial/TrialList.vue"),
                    meta: { category: "Experiments", requireAuth: true }
                },
                {
                    path: "/trial/trial-detail/:id",
                    name: "TrialDetail",
                    component: () => import("@/components/Trial/TrialDetail.vue"),
                    props: true,
                    meta: { category: "Experiments", requireAuth: true }
                },
                {
                    path: "/palm/palm-list",
                    name: "PalmList",
                    component: () => import("@/components/Palm/PalmList.vue"),
                    meta: { category: "Experiments", requireAuth: true }
                },
                {
                    path: "/palm/palm-details/:id",
                    name: "PalmDetail",
                    component: () => import("@/components/Palm/PalmDetail.vue"),
                    props: true,
                    meta: { category: "Experiments", requireAuth: true }
                },
                {
                    path: "/palm/palm-benchmarks-details/:id",
                    name: "PalmBenchmarkDetail",
                    component: () => import("@/components/Palm/PalmBenchmarkDetail.vue"),
                    props: true,
                    meta: { category: "Experiments", requireAuth: true }
                },
                {
                    path: "/palm/palm-raw-details/:id",
                    name: "PalmRawDetail",
                    component: () => import("@/components/Palm/PalmRawData/PalmRawDetail.vue"),
                    props: true,
                    meta: { category: "Experiments", requireAuth: true }
                },
                //PROGRAMMES
                {
                    path: "/programme/programme-list",
                    name: "ProgrammeList",
                    component: () => import("@/components/Programme/ProgrammeList.vue"),
                    meta: { category: "Programmes", requireAuth: true }
                },
                {
                    path: "/programme/programme-detail/:id",
                    name: "ProgrammeDetail",
                    component: () => import("@/components/Programme/ProgrammeDetail.vue"),
                    props: true,
                    meta: { category: "Programmes", requireAuth: true }
                },
                {
                    path: "/programme/create",
                    name: "NewProgramme",
                    component: () => import("@/components/Programme/NewProgramme/NewProgramme.vue"),
                    props: true,
                    meta: { category: "Programmes", requireAuth: true }
                },
                {
                    path: "/programme/create-new-crossing-programme",
                    name: "NewCrossingProgramme",
                    component: () => import("@/components/Programme/NewCrossingProgramme.vue"),
                    props: true,
                    meta: { category: "Programmes", requireAuth: true }
                },
                {
                    path: "/progeny/progeny-list",
                    name: "ProgenyList",
                    component: () => import("@/components/Progeny/ProgenyList.vue"),
                    meta: { category: "Programmes", requireAuth: true }
                },
                {
                    path: "/progeny/progeny-population-list",
                    name: "Progeny Population List",
                    component: () => import("@/components/Progeny/ProgenyPopulationList.vue"),
                    meta: { category: "Programmes", requireAuth: true }
                },
                {
                    path: "/progeny/progeny-detail/:id",
                    name: "ProgenyDetail",
                    component: () => import("@/components/Progeny/ProgenyDetail.vue"),
                    props: true,
                    meta: { category: "Programmes", requireAuth: true }
                },
                //LISTS
                {
                    path: "/agency/agency-list",
                    name: "AgencyList",
                    component: () => import("@/components/Agency/AgencyList.vue"),
                    meta: { category: "Lists", requireAuth: true }
                },
                {
                    path: "/agency/agency-detail/:id",
                    name: "AgencyDetail",
                    component: () => import("@/components/Agency/AgencyDetail.vue"),
                    props: true,
                    meta: { category: "Lists", requireAuth: true }
                },
                {
                    path: "/germplasm/germplasm-list/:countryId",
                    name: "GermplasmList",
                    component: () => import("@/components/Germplasm/GermplasmList.vue"),
                    meta: { category: "Lists", requireAuth: true }
                },
                {
                    path: "/germplasm/germplasm-create",
                    name: "NewGermplasm",
                    component: () => import("@/components/Germplasm/NewGermplasm/NewGermplasm.vue"),
                    props: true,
                    meta: { category: "Lists", requireAuth: true }
                },
                {
                    path: "/germplasm/germplasm-detail/:id",
                    name: "GermplasmDetail",
                    component: () => import("@/components/Germplasm/GermplasmDetail.vue"),
                    props: true,
                    meta: { category: "Lists", requireAuth: true }
                },
        
                // INVENTORY
                {
                    path: "/pollen-inventory",
                    name: "PollenInventory",
                    component: () => import("@/components/Inventory/PollenInventory.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/pollen",
                    name: "Pollen",
                    component: () => import("@/components/Pollen/PollenList.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/pollen-bottle",
                    name: "PollenBottle",
                    component: () => import("@/components/PollenBottle/PollenBottleList.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/pollen-bottle-transaction",
                    name: "PollenBottleTransaction",
                    component: () => import("@/components/PollenBottleTransaction/PollenBottleTransactionList.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/germination-batch",
                    name: "GerminationBatch",
                    component: () => import("@/components/GerminationBatch/GerminationBatchList.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/germplasm-inventory",
                    name: "GermplasmInventory",
                    component: () => import("@/components/Inventory/GermplasmInventory.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/fresh-seed-bag-transaction",
                    name: "FreshSeedBagTransaction",
                    component: () => import("@/components/FreshSeedTransaction/SeedTransaction.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/fresh-seed-bag-transaction/fresh-seed-transaction-detail/:id",
                    name: "FreshSeedTransactionDetail",
                    component: () => import("@/components/FreshSeedTransaction/FreshSeedTransactionDetail.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/germinated-seed-bag-transaction",
                    name: "GerminatedSeedTransaction",
                    component: () => import("@/components/GerminatedSeedTransaction/GerminatedSeedTransaction.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/germinated-seed-bag-transaction/germinated-seed-transaction-detail/:id",
                    name: "GerminatedSeedTransactionDetail",
                    component: () => import("@/components/GerminatedSeedTransaction/GerminatedSeedTransactionDetail.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/fresh-seed-bag-list",
                    name: "FreshSeedBagList",
                    component: () => import("@/components/FreshSeedBag/FreshSeedBagList.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/fresh-seed-bag/fresh-seed-bag-detail/:id",
                    name: "FreshSeedBagDetail",
                    component: () => import("@/components/FreshSeedBag/FreshSeedBagDetail.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/germinated-seed-bag-list",
                    name: "GerminatedSeedBagList",
                    component: () => import("@/components/GerminatedSeedBag/GerminatedSeedBagList.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/germinated-seed-bag/germinated-seed-bag-detail/:id",
                    name: "GerminatedSeedBagDetail",
                    component: () => import("@/components/GerminatedSeedBag/GerminatedSeedBagDetail.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                //SEARCH
                {
                    path: "/search",
                    name: "AdvanceSearch",
                    component: () => import("@/components/AdvanceSearch/AdvanceSearch.vue"),
                    meta: { category: "SearchItems", requireAuth: true }
                },
                {
                    path: "/global-search",
                    name: "GlobalSearch",
                    component: () => import("@/components/AdvanceSearch/GlobalSearch.vue"),
                    meta: { category: "SearchItems", requireAuth: true }
                },
                // ANALYSIS
                {
                    path: "/trials-analysis",
                    name: "TrialAnalysis",
                    component: () => import("@/components/Analysis/TrialAnalysis.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/yield-anova-analysis",
                    name: "YieldAnovaAnalysis",
                    component: () => import("@/components/Analysis/YieldAnova/YieldAnova.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/progeny-analysis",
                    name: "ProgenyAnalysis",
                    component: () => import("@/components/Analysis/ProgenyAnalysis.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/breeding-material-selection",
                    name: "BreedingMaterialSelection",
                    component: () => import("@/components/Analysis/BreedingMaterialSelection/BreedingMaterialSelection.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/yield-anova",
                    name: "YieldAnova",
                    component: () => import("@/components/Analysis/YieldAnova/YieldAnova.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/field-performance",
                    name: "FieldPerformance",
                    component: () => import("@/components/Analysis/FieldPerformance.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/physical-inventory",
                    name: "PhysicalInventory",
                    component: () => import("@/components/Analysis/PhysicalInventory.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/seed-inventory",
                    name: "SeedInventory",
                    component: () => import("@/components/Analysis/SeedInventory.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/germplasm-collection",
                    name: "GermplasmCollection",
                    component: () => import("@/components/Analysis/GermplasmCollection.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                {
                    path: "/programme-overview",
                    name: "ProgrammeOverview",
                    component: () => import("@/components/Analysis/ProgrammeOverview.vue"),
                    meta: { category: "Analysis", requireAuth: true }
                },
                //CONFIGURATIONS
                {
                    path: "/base-configuration",
                    name: "BaseConfiguration",
                    component: () => import("@/components/Configurations/BaseConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true }
                },
                {
                    path: "/types-configuration",
                    name: "BaseTypesConfiguration",
                    component: () => import("@/components/Configurations/TypesConfiguration/BaseTypesConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true }
                },
                {
                    path: "/others-configuration",
                    name: "BaseOtherConfiguration",
                    component: () => import("@/components/Configurations/OtherConfiguration/BaseOtherConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true }
                },
                {
                    path: "/palm-series/series-traits/:id",
                    name: "SeriesTraits",
                    component: () => import("@/components/Configurations/AnalyticsConfiguration/PalmSeriesConfiguration/SeriesTraitConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true }
                },
                {
                    path: "/standard/standard-benchmark/:id",
                    name: "StnadardBenchmark",
                    component: () => import("@/components/Configurations/AnalyticsConfiguration/StandardConfiguration/StandardBenchmarkConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true }
                },
                // Glossary
                {
                    path: "/glossary",
                    name: "Glossary",
                    component: () => import("@/components/Glossary/Glossary.vue"),
                    meta: { category: "Glossary", requireAuth: true }
                },
                // UMS
                {
                    path: "/my-profile",
                    name: "MyProfile",
                    component: () => import("@/components/UMS/MyProfile.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/user-management",
                    name: "UserManagement",
                    component: () => import("@/components/UMS/UserManagement/UserManagement.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/system-log",
                    name: "AdvanceLog",
                    component: () => import("@/components/UMS/AuditLog/AdvanceLog.vue"),
                    meta: { category: "", requireAuth: true, permissionName: "Permissions.Breeding.AuditLogs" }
                },
                {
                    path: "/activity-log",
                    name: "SimpleLog",
                    component: () => import("@/components/UMS/AuditLog/SimpleLog.vue"),
                    meta: { category: "", requireAuth: true, permissionName: "Permissions.Breeding.SimpleLogs" }
                },
                {
                    path: "/trial-management",
                    name: "TrialManagement",
                    component: () => import("@/components/UMS/TrialManagement/TrialManagement.vue"),
                    meta: { category: "", requireAuth: true, permissionName: "Permissions.Breeding.TrialRoles" }
                },
                {
                    path: "/role-permission-table",
                    name: "RolePermissionsTable",
                    component: () => import("@/components/UMS/RoleManagement/RolePermissionsTable.vue"),
                    meta: { category: "", requireAuth: true, permissionName: "Permissions.Breeding.Users" }
                },
                // Raw
                {
                    path: "/raw/bunch-data-list",
                    name: "BunchDataRawList",
                    component: () => import("@/components/Raw/BunchDataRawList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/carotene-data-list",
                    name: "CaroteneDataRawList",
                    component: () => import("@/components/Raw/CaroteneDataRawList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/fatty-acid-data-list",
                    name: "FattyAcidDataRawList",
                    component: () => import("@/components/Raw/FattyAcidDataRawList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/vegetative-list",
                    name: "VegetativeRawList",
                    component: () => import("@/components/Raw/VegetativeRawList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/vitamin-list",
                    name: "VitaminRawList",
                    component: () => import("@/components/Raw/VitaminRawList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/yield-data-list",
                    name: "YieldRawList",
                    component: () => import("@/components/Raw/YieldRawList.vue"),
                    meta: { category: "", requireAuth: true }
                },
        
                // Derived
                {
                    path: "/derived/bunch-data-list",
                    name: "BunchDataDerivedList",
                    component: () => import("@/components/Derived/BunchDataDerivedList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/derived/vegetative-list",
                    name: "VegetativeDerivedList",
                    component: () => import("@/components/Derived/VegetativeDerivedList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/derived/yield-data-list",
                    name: "YieldDerivedList",
                    component: () => import("@/components/Derived/YieldDerivedList.vue"),
                    meta: { category: "", requireAuth: true }
                },
                // Raw Detail
                {
                    path: "/raw/bunch-data-detail/:id",
                    name: "RawBunchDetail",
                    component: RawBunchDetail,
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/carotene-data-detail/:id",
                    name: "RawCaroteneDetail",
                    component: RawCaroteneDetail,
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/fatty-acid-data-detail/:id",
                    name: "RawFattyAcidDetail",
                    component: RawFattyAcidDetail,
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/vegetative-data-detail/:id",
                    name: "RawVegetativeDetail",
                    component: RawVegetativeDetail,
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/vitamin-data-detail/:id",
                    name: "RawVitaminDetail",
                    component: RawVitaminDetail,
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/raw/yield-data-detail/:id",
                    name: "RawYieldDetail",
                    component: RawYieldDetail,
                    meta: { category: "", requireAuth: true }
                },
                // Import 
                {
                    path: "/import-experiment",
                    name: "ImportExperiment",
                    component: () => import("@/components/Action/ImportExperiment.vue"),
                    meta: { category: "", requireAuth: true }
                },
                // {
                //     path: "/import-raw-data",
                //     name: "ImportRawData",
                //     component: () => import("@/components/Action/ImportRawData.vue"),
                //     meta: { category: "", requireAuth: true }
                // },
                // {
                //     path: "/import-derived-data",
                //     name: "ImportDerivedData",
                //     component: () => import("@/components/Action/ImportDerivedData.vue"),
                //     meta: { category: "", requireAuth: true }
                // },
                {
                    path: "/import-status",
                    name: "ImportStatus",
                    component: () => import("@/components/Action/ImportStatus/ImportStatus.vue"),
                    meta: { category: "", requireAuth: true }
                },
                {
                    path: "/input-raw-data",
                    name: "InputRawData",
                    component: () => import("@/components/Action/InputRawData.vue"),
                    meta: { category: "", requireAuth: true }
                },
                // Export
                {
                    path: "/export-file",
                    name: "ExportedFile",
                    component: () => import("@/components/ExportedFile/ExportedList.vue"),
                    meta: { category: "Exprot", requireAuth: true }
                },
                // Trial User
                {
                    path: "/trial/:id/trial-user",
                    name: "TrialUser",
                    component: () => import("@/components/Trial/TrialUser.vue"),
                    meta: { category: "TrialUser", requireAuth: true }
                },
                // Internal 
                {
                    path: "/raw-flag-configuration",
                    name: "RawFlagConfiguration",
                    component: () => import("@/components/Configurations/RawFlagConfiguration/RawFlagConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/group-flag-configuration",
                    name: "GroupFlagConfiguration",
                    component: () => import("@/components/Configurations/GroupFlagConfiguration/GroupFlagConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/raw/other-recording",
                    name: "OtherRecordingList",
                    component: () => import("@/components/Raw/OtherRecordingList.vue"),
                    meta: { category: "", requireAuth: true, internal: true }
                },
                {
                    path: "/custom-form-configuration",
                    name: "CustomFormConfiguration",
                    component: () => import("@/components/Configurations/CustomFormConfiguration/CustomFormConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/custom-form-configuration/:id",
                    name: "CustomFormConfigurationDetail",
                    component: () => import("@/components/Configurations/CustomFormConfiguration/CustomFormConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/field-data-form-configuration",
                    name: "FieldDataFormConfiguration",
                    component: () => import("@/components/Configurations/CustomFormConfiguration/FieldDataFormConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/field-data-form-configuration/:id",
                    name: "FieldDataFormConfigurationDetail",
                    component: () => import("@/components/Configurations/CustomFormConfiguration/FieldDataFormConfiguration.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/custom-form-table",
                    name: "CustomFormTable",
                    component: () => import("@/components/Configurations/CustomFormConfiguration/CustomFormTable.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/darwin-form-configuration",
                    name: "DarwinFormConfiguration",
                    component: () => import("@/components/Darwin/CustomForm.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                {
                    path: "/measurement-unit-configuration",
                    name: "MeasurementUnitConfiguration",
                    component: () => import("@/components/Configurations/MeasurementUnitConfiguration/MeasurementUnitConf.vue"),
                    meta: { category: "Configurations", requireAuth: true, internal: true }
                },
                // Seed Production
                {
                    path: "/seed-production-form",
                    name: "SeedProductionForm",
                    component: () => import("@/components/SeedProductionForms/index.vue"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/bunches",
                    name: "Bunch",
                    component: () => import("@/components/Bunch/index.vue"),
                    meta: { category: "Bunch", requireAuth: true }
                },
                {
                    path: "/form-recording/:formId",
                    name: "FormRecording",
                    component: () => import("@/components/FormRecording/index.vue"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/edit-darwin-form-configuration/:formId",
                    name: "EditForm",
                    hidden: true,
                    component: () => import("@/components/Darwin/editForm.vue"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/fresh-seed-batching",
                    name: "Batching",
                    hidden: true,
                    component: () => import("@/components/FreshSeedBatching"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/bunch-treatment",
                    name: "Bunch Treatment",
                    hidden: true,
                    component: () => import("@/components/BunchTreatment"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/fresh-seed-treatment",
                    name: "Fresh Seed Treatment",
                    hidden: true,
                    component: () => import("@/components/FreshSeedBag/FreshSeedBagTreatment"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/moisture-content-1",
                    name: "Moisture Content 1",
                    hidden: true,
                    component: () => import("@/components/GerminatedSeedBag/MoistureContent1"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/germination-batch-treatment",
                    name: "Germination Batch Treatment",
                    hidden: true,
                    component: () => import("@/components/GerminationBatchTreatment"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/moisture-content-2",
                    name: "Moisture Content 2",
                    hidden: true,
                    component: () => import("@/components/GerminatedSeedBag/MoistureContent2"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/germination-sort-record-list",
                    name: "Germination Sort Record List",
                    component: () => import("@/components/GerminatedSortRecord/GerminationSortRecordList.vue"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/germination-sort-record",
                    name: "Germination Sort Record",
                    component: () => import("@/components/GerminatedSortRecord"),
                    meta: { category: "Inventories", requireAuth: true }
                },
                {
                    path: "/pollen-quality-control",
                    name: "Pollen Lab Quality Control",
                    hidden: true,
                    component: () => import("@/components/PollenLabQualityControl"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/processing-one",
                    name: "PollenProcessing1",
                    hidden: true,
                    component: () => import("@/components/Pollen/PollenProcessing1"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/processing-two",
                    name: "PollenProcessing2",
                    hidden: true,
                    component: () => import("@/components/Pollen/PollenProcessing2"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/pollen-bottle-discard-authorization",
                    name: "Pollen Bottle Discard Authorization",
                    hidden: true,
                    component: () => import("@/components/PollenBottle/PollenBottleDiscardAuthorization"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
                {
                    path: "/data-import",
                    name: "DataImport",
                    hidden: true,
                    component: () => import("@/components/DataImport"),
                    meta: { category: "SeedProductionForm", requireAuth: true }
                },
            ]
        },
        // Dashboard
        
    ]
});


//Check the route path with REQUIRE AUTH, if dont have Token, redirect to login page 
router.beforeEach((to, from, next) => {
    closeInfoSidebar();
    clearGeneralFilter();
    
    if (to.meta.requireAuth === true) {
        const accessToken = TokenService.getToken();

        if (accessToken) {
            if (to.meta.permissionName) {
                console.log("to.meta.permissionName", to.meta.permissionName);
                if ( PermissionService.checkPermission(to.meta.permissionName) ) {
                    next();
                }
                else {
                    EventBus.$emit("snackBar", {
                        text: "You have no permission to access this page",
                        snackbarColor: "red darken-2"
                    });
                    next({ name: "Home" });
                }
            }
            else {
                next();
            }

            if (to.meta.internal) {
                if (process.env.INTERNAL) {
                    next();
                }
                else {
                    next({ name: "Error404" });
                }
            }
        }
        else {
            next({ name: "Login" });
        }
    }
    else if (to.matched.length == 0) {
        next({ name: "Error404" });
    }
    else {
        next();
    }
});


router.beforeResolve((to, from, next) => {
    // If this isn't an initial page load.
    if (to.name) {
        // Start the route progress bar.
        setTimeout(function() { $(".spinner-wrapper").show(); }, 100);

        window.stop(); //stop all the pending request
    }
    next();
});

router.afterEach((to, from) => {
    // Complete the animation of the route progress bar.
    setTimeout(function() { $(".spinner-wrapper").hide(); }, 100);
    closeInfoSidebar();
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js